/**
 * Root render + navigation. Renders the top nav and delegates the main area to
 * the active screen. A screen is a function that populates a mount element and
 * may re-render itself by mutating the DOM directly; global navigation always
 * goes through the store, which triggers a full root re-render.
 */

import type { Store, ScreenName, AppState } from "./state";

export type Screen = (store: Store, mount: HTMLElement) => void;

const NAV_ITEMS: Array<{ id: ScreenName; label: string }> = [
  { id: "home", label: "Home" },
  { id: "learn", label: "Learn" },
  { id: "practice", label: "Practice" },
  { id: "quiz", label: "Quiz" },
  { id: "progress", label: "Progress" },
];

export class Router {
  constructor(
    private readonly root: HTMLElement,
    private readonly store: Store,
    private readonly screens: Record<ScreenName, Screen>,
  ) {
    this.store.subscribe(() => this.render());
  }

  render(): void {
    const state = this.store.get();
    this.root.replaceChildren();
    this.root.appendChild(this.renderNav(state));

    const main = document.createElement("main");
    main.id = "screen";
    this.root.appendChild(main);

    const screen = this.screens[state.screen];
    screen(this.store, main);
  }

  private renderNav(state: AppState): HTMLElement {
    const nav = document.createElement("nav");
    nav.className = "nav";
    nav.setAttribute("aria-label", "Primary");

    const inner = document.createElement("div");
    inner.className = "nav-inner";

    const brand = document.createElement("button");
    brand.className = "nav-brand";
    brand.setAttribute("aria-label", "AXLE home");
    const logo = document.createElement("img");
    logo.className = "nav-logo";
    logo.src = "/axle-logo.png";
    logo.alt = "AXLE";
    logo.width = 96;
    logo.height = 64;
    brand.appendChild(logo);
    brand.addEventListener("click", () => this.store.navigate("home"));
    inner.appendChild(brand);

    for (const item of NAV_ITEMS) {
      const btn = document.createElement("button");
      btn.className = "nav-link";
      btn.textContent = item.label;
      if (state.screen === item.id) btn.setAttribute("aria-current", "page");
      btn.addEventListener("click", () => this.store.navigate(item.id));
      inner.appendChild(btn);
    }

    nav.appendChild(inner);
    return nav;
  }
}
